import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2, Plus, UserPlus } from "lucide-react";
import api from "@/lib/api";
import { AdminLayout } from "@/pages/admin/AdminLayout";

export default function Settings() {
  const [config, setConfig] = useState(null);
  const [inq, setInq] = useState({ advertise: [], contact: [] });
  useEffect(() => {
    api.get("/agent/status").then((r) => setConfig(r.data.config)).catch(() => {});
    api.get("/admin/inquiries").then((r) => setInq(r.data)).catch(() => {});
  }, []);

  const TYPES = ["Explainer", "Industry Brief", "Company Profile"];
  const toggleType = async (t) => {
    const list = config.auto_publish_types || [];
    const next = list.includes(t) ? list.filter(x => x !== t) : [...list, t];
    await api.put("/agent/config", { auto_publish_types: next });
    setConfig({ ...config, auto_publish_types: next }); toast.success("Updated");
  };

  return (
    <AdminLayout title="Settings">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-admcard border border-admborder p-5">
          <h3 className="text-xs font-bold uppercase tracking-widest text-admtext2 mb-3">Auto-publish safe content types</h3>
          <p className="text-xs text-admmuted mb-3">Auto-publishing is OFF by default. Even when enabled, only the selected safe evergreen types are eligible; breaking news always requires human review.</p>
          {config && TYPES.map((t) => (
            <label key={t} className="flex items-center gap-3 py-2 border-b border-admborder cursor-pointer">
              <input type="checkbox" checked={(config.auto_publish_types||[]).includes(t)} onChange={() => toggleType(t)} className="accent-cyan" />
              <span className="text-sm text-admtext">{t}</span>
            </label>
          ))}
        </div>

        <div className="bg-admcard border border-admborder p-5">
          <h3 className="text-xs font-bold uppercase tracking-widest text-admtext2 mb-3">Publication</h3>
          <dl className="text-sm space-y-2">
            <div className="flex justify-between border-b border-admborder py-1.5"><dt className="text-admmuted">Title</dt><dd className="text-admtext">The Fin Desk</dd></div>
            <div className="flex justify-between border-b border-admborder py-1.5"><dt className="text-admmuted">Tagline</dt><dd className="text-admtext">Finance. Fintech. Payments. Intelligence.</dd></div>
            <div className="flex justify-between border-b border-admborder py-1.5"><dt className="text-admmuted">Newsletter</dt><dd className="text-admtext">The Fin Desk Daily</dd></div>
            <div className="flex justify-between py-1.5"><dt className="text-admmuted">Editorial agent</dt><dd className="text-cyan">The Fin Desk Editor</dd></div>
          </dl>
        </div>
      </div>

      <AuthorsPanel />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
        <InquiryList title="Advertising Enquiries" items={inq.advertise} render={(i) => `${i.name} · ${i.company} — ${i.interest}`} />
        <InquiryList title="Contact Messages" items={inq.contact} render={(i) => `${i.name} — ${i.subject || "(no subject)"}`} />
      </div>
    </AdminLayout>
  );
}

const EMPTY_AUTHOR = { name: "", role: "", bio: "", expertise: "", photo: "", linkedin: "", twitter: "" };

function AuthorsPanel() {
  const [authors, setAuthors] = useState([]);
  const [form, setForm] = useState(EMPTY_AUTHOR);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/authors").then((r) => setAuthors(r.data || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async () => {
    if (!form.name.trim()) return toast.error("Name is required");
    setSaving(true);
    try {
      await api.post("/admin/authors", {
        ...form,
        expertise: form.expertise.split(",").map((s) => s.trim()).filter(Boolean),
      });
      toast.success("Author added");
      setForm(EMPTY_AUTHOR); setOpen(false); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail ? "Validation error" : "Failed to add author");
    } finally { setSaving(false); }
  };
  const remove = async (a) => {
    if (!window.confirm(`Delete author "${a.name}"?`)) return;
    try { await api.delete(`/admin/authors/${a.id}`); toast.success("Author deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  const inp = "w-full bg-admbg border border-admborder px-3 py-2 text-admtext text-sm outline-none focus:border-cyan";
  const lbl = "text-[10px] font-bold uppercase tracking-widest text-admtext2 mb-1 block";

  return (
    <div className="bg-admcard border border-admborder p-5 mt-4" data-testid="authors-panel">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-xs font-bold uppercase tracking-widest text-admtext2">Authors</h3>
        <button onClick={() => setOpen(!open)} data-testid="toggle-add-author" className="flex items-center gap-1.5 bg-cyan text-admbg px-3 py-1.5 text-xs font-bold uppercase tracking-wider hover:opacity-90">
          {open ? <>Close</> : <><UserPlus size={13} /> Add Author</>}
        </button>
      </div>

      {open && (
        <div className="border border-admborder p-4 mb-4 grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="add-author-form">
          <div><label className={lbl}>Name *</label><input className={inp} value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="author-name" placeholder="e.g. Jane Doe" /></div>
          <div><label className={lbl}>Role</label><input className={inp} value={form.role} onChange={(e) => set("role", e.target.value)} data-testid="author-role" placeholder="e.g. Payments Correspondent" /></div>
          <div className="md:col-span-2"><label className={lbl}>Bio</label><textarea className={inp} rows={2} value={form.bio} onChange={(e) => set("bio", e.target.value)} data-testid="author-bio" /></div>
          <div className="md:col-span-2"><label className={lbl}>Expertise (comma separated)</label><input className={inp} value={form.expertise} onChange={(e) => set("expertise", e.target.value)} data-testid="author-expertise" placeholder="payments, banking, fintech" /></div>
          <div><label className={lbl}>Photo URL</label><input className={inp} value={form.photo} onChange={(e) => set("photo", e.target.value)} data-testid="author-photo" /></div>
          <div><label className={lbl}>LinkedIn</label><input className={inp} value={form.linkedin} onChange={(e) => set("linkedin", e.target.value)} data-testid="author-linkedin" /></div>
          <div><label className={lbl}>X / Twitter</label><input className={inp} value={form.twitter} onChange={(e) => set("twitter", e.target.value)} data-testid="author-twitter" /></div>
          <div className="md:col-span-2 flex justify-end">
            <button onClick={submit} disabled={saving} data-testid="save-author" className="flex items-center gap-1.5 bg-emerald text-admbg px-4 py-2 text-xs font-bold uppercase tracking-wider hover:opacity-90 disabled:opacity-40"><Plus size={13} /> {saving ? "Saving…" : "Save Author"}</button>
          </div>
        </div>
      )}

      <div className="space-y-2" data-testid="authors-list">
        {authors.map((a) => (
          <div key={a.id} className="flex items-center gap-3 border-b border-admborder py-2" data-testid={`author-row-${a.id}`}>
            {a.photo
              ? <img src={a.photo} alt={a.name} className="w-9 h-9 rounded-full object-cover border border-admborder" />
              : <div className="w-9 h-9 rounded-full bg-admbg border border-admborder flex items-center justify-center text-admmuted text-xs font-bold">{(a.name || "?").slice(0, 1)}</div>}
            <div className="min-w-0 flex-1">
              <div className="text-sm text-admtext truncate">{a.name} {a.institutional && <span className="text-[10px] text-cyan uppercase tracking-wider ml-1">Institutional</span>}</div>
              <div className="text-xs text-admmuted truncate">{a.role || "—"} · {a.article_count} article{a.article_count === 1 ? "" : "s"}</div>
            </div>
            <button onClick={() => remove(a)} data-testid={`delete-author-${a.id}`} className="text-crimson hover:opacity-80 p-1" title="Delete author"><Trash2 size={15} /></button>
          </div>
        ))}
        {!authors.length && <div className="text-admmuted text-sm">No authors yet.</div>}
      </div>
    </div>
  );
}

function InquiryList({ title, items, render }) {
  return (
    <div className="bg-admcard border border-admborder p-5">
      <h3 className="text-xs font-bold uppercase tracking-widest text-admtext2 mb-3">{title}</h3>
      <div className="max-h-72 overflow-auto no-scrollbar space-y-2">
        {items.map((i) => (
          <div key={i.id} className="border-b border-admborder py-2">
            <div className="text-sm text-admtext">{render(i)}</div>
            <div className="text-xs text-admmuted font-mono">{i.email}</div>
            {i.message && <div className="text-xs text-admtext2 mt-1 line-clamp-2">{i.message}</div>}
          </div>
        ))}
        {!items.length && <div className="text-admmuted text-sm">None yet.</div>}
      </div>
    </div>
  );
}
